import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Card, Button, Badge } from './ui';
import groupService from '../services/groupService';
import {
  XMarkIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  PauseIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const AccountScannerProcessMonitor = ({ processId, onClose }) => {
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch process status with auto-refresh
  const {
    data: processStatus,
    isLoading,
    refetch
  } = useQuery(
    ['accountScannerProcess', processId],
    () => groupService.getAccountScannerProcessStatus(processId),
    {
      enabled: !!processId,
      refetchInterval: autoRefresh ? 2000 : false,
      refetchIntervalInBackground: false,
      onError: (err) => {
        console.error('Error fetching account scanner process status:', err);
      }
    }
  );

  // Auto-close when process completes
  useEffect(() => {
    if (processStatus?.status === 'completed' || processStatus?.status === 'failed') {
      const timer = setTimeout(() => {
        setAutoRefresh(false);
      }, 10000); // Stop auto-refresh after 10 seconds when completed

      return () => clearTimeout(timer);
    }
  }, [processStatus?.status]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'in_progress':
        return <Badge variant="yellow">Scanning</Badge>;
      case 'completed':
        return <Badge variant="green">Completed</Badge>;
      case 'failed':
        return <Badge variant="red">Failed</Badge>;
      case 'cancelled':
        return <Badge variant="gray">Cancelled</Badge>;
      default:
        return <Badge variant="gray">Unknown</Badge>;
    }
  };

  const getProgressPercentage = () => {
    if (!processStatus || !processStatus.totalAccounts) return 0;
    // Validate progress doesn't exceed 100%
    const percentage = Math.round((processStatus.processed / processStatus.totalAccounts) * 100);
    return Math.min(percentage, 100);
  };

  const getProgressText = () => {
    if (!processStatus) return '';
    const processed = processStatus.processed || 0;
    const total = processStatus.totalAccounts || 0;
    return `${processed}/${total} accounts`;
  };

  const cancelProcess = async () => {
    try {
      await groupService.cancelAccountScannerProcess(processId);
      refetch();
    } catch (error) {
      console.error('Error cancelling account scanner process:', error);
    }
  };

  const getLogIcon = (logType) => {
    switch (logType) {
      case 'info':
        return <ClockIcon className="h-3 w-3 text-gray-500" />;
      case 'account_start':
        return <UserGroupIcon className="h-3 w-3 text-blue-500" />;
      case 'account_complete':
        return <CheckCircleIcon className="h-3 w-3 text-green-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-3 w-3 text-yellow-500" />;
      case 'error':
        return <ExclamationTriangleIcon className="h-3 w-3 text-red-500" />;
      case 'success':
        return <CheckCircleIcon className="h-3 w-3 text-green-600" />;
      case 'summary':
        return <CheckCircleIcon className="h-3 w-3 text-blue-600" />;
      default:
        return <ClockIcon className="h-3 w-3 text-gray-400" />;
    }
  };

  if (isLoading && !processStatus) {
    return (
      <Card className="mb-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">Loading account scanner status...</div>
          <Button size="sm" variant="secondary" onClick={onClose}>
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    );
  }

  if (!processStatus) {
    return (
      <Card className="mb-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-red-600">Account scanner process not found</div>
          <Button size="sm" variant="secondary" onClick={onClose}>
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-medium text-gray-900">
              Account Scanner Process
            </h3>
            {getStatusBadge(processStatus.status)}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setAutoRefresh(!autoRefresh)}
              icon={autoRefresh ? PauseIcon : PlayIcon}
            >
              {autoRefresh ? 'Pause' : 'Resume'}
            </Button>
            <Button size="sm" variant="secondary" onClick={onClose}>
              <XMarkIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        <div>
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Progress: {getProgressText()}</span>
            <span>{getProgressPercentage()}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                processStatus.status === 'completed'
                  ? 'bg-green-600'
                  : processStatus.status === 'failed'
                  ? 'bg-red-600'
                  : 'bg-blue-600'
              }`}
              style={{ width: `${getProgressPercentage()}%` }}
            ></div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-gray-900">{processStatus.totalAccounts || 0}</div>
            <div className="text-sm text-gray-500">Accounts</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">{processStatus.processed || 0}</div>
            <div className="text-sm text-gray-500">Processed</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">{processStatus.totalNewGroups || 0}</div>
            <div className="text-sm text-gray-500">New Groups</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-purple-600">{processStatus.totalUsernameUpdates || 0}</div>
            <div className="text-sm text-gray-500">Usernames</div>
          </div>
        </div>

        {/* Process Info */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">Process ID:</span>
            <span className="ml-2 text-gray-600 font-mono">{processStatus.processId}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Started:</span>
            <span className="ml-2 text-gray-600">
              {new Date(processStatus.startedAt).toLocaleString()}
            </span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Concurrent:</span>
            <span className="ml-2 text-gray-600">
              {processStatus.config?.maxConcurrent || 2} accounts
            </span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Delay:</span>
            <span className="ml-2 text-gray-600">
              {processStatus.config?.delayBetween ? `${processStatus.config.delayBetween / 1000}s` : '5s'}
            </span>
          </div>
        </div>

        {/* Current Accounts Being Processed */}
        {processStatus.accounts && processStatus.accounts.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Account Results</h4>
            <div className="bg-gray-50 rounded-md p-3 max-h-32 overflow-y-auto">
              <div className="space-y-1">
                {processStatus.accounts.slice(-5).map((account, index) => (
                  <div key={index} className="text-xs flex justify-between">
                    <span className="text-gray-700">{account.accountName}</span>
                    <div className="flex space-x-2">
                      <span className="text-green-600">+{account.addedToDatabase}</span>
                      <span className="text-purple-600">@{account.usernameUpdates || 0}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        {processStatus.status === 'in_progress' && (
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="danger"
              onClick={cancelProcess}
            >
              Cancel Scan
            </Button>
          </div>
        )}

        {/* Logs */}
        {processStatus.logs && processStatus.logs.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700">
                Process Logs ({processStatus.logs.length} total)
              </h4>
              <div className="text-xs text-gray-500">
                Showing all activity
              </div>
            </div>
            <div className="bg-gray-50 rounded-md p-3 max-h-96 overflow-y-auto border">
              <div className="space-y-1">
                {processStatus.logs.map((log, index) => (
                  <div key={index} className="text-xs flex items-start hover:bg-gray-100 p-1 rounded">
                    <div className="flex items-center mr-2 mt-0.5 flex-shrink-0">
                      {getLogIcon(log.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-gray-500 font-mono text-xs">
                        {new Date(log.time).toLocaleTimeString()}
                      </span>
                      <span className="ml-2 text-gray-700 break-words">{log.message}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default AccountScannerProcessMonitor;
